"use client";

import { useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { Search, Users } from "lucide-react";
import { toEsEn, type Locale } from "@/i18n/config";
import { formatearPrecio } from "../../../lib/api/taxi/types";
import tarifasTaxi from "./tarifas.json";

/** Para buscar sin que estorben tildes ni mayúsculas. */
function normalizar(texto: string): string {
  return texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

/**
 * Listado de precios de referencia mientras la reserva en línea está apagada
 * (ver `RESERVAS_ACTIVAS` en la página). Los datos son estáticos a propósito:
 * son las mismas tarifas que sirve /api/taxi/tarifas, recortadas al precio base
 * —el que cubre 2 pasajeros— para poder enseñarlas sin el formulario.
 */
export default function TarifasTaxi() {
  const t = useTranslations("taxiPage");
  const locale = useLocale() as Locale;
  // El catálogo sólo viene en es/en, como el resto de datos del proveedor.
  const idioma = toEsEn(locale);
  const [busqueda, setBusqueda] = useState("");

  const consulta = normalizar(busqueda.trim());
  const filtradas = consulta
    ? tarifasTaxi.tarifas.filter((tarifa) =>
        normalizar(tarifa.destino[idioma]).includes(consulta),
      )
    : tarifasTaxi.tarifas;

  return (
    <section id="tarifas-taxi" className="w-full mt-24 scroll-mt-24">
      <div className="text-center mb-12">
        <span className="text-[#ffb800] text-sm uppercase tracking-widest font-semibold">
          {t("tarifasLabel")}
        </span>
        <h2 className="text-3xl md:text-4xl font-bold text-[#102542] mt-3 mb-3">
          {t("tarifasTitle")}
        </h2>
        <p className="text-gray-600 max-w-2xl mx-auto">{t("tarifasDesc")}</p>
      </div>

      <div className="mx-auto max-w-3xl">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
          <div className="relative flex-1">
            <Search
              className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
              aria-hidden
            />
            <input
              type="search"
              value={busqueda}
              onChange={(evento) => setBusqueda(evento.target.value)}
              placeholder={t("tarifasBuscar")}
              aria-label={t("tarifasBuscar")}
              aria-controls="tabla-tarifas-taxi"
              className="w-full rounded-xl border border-gray-200 bg-white py-3 pl-11 pr-4 text-sm text-[#102542] shadow-sm outline-none transition-colors placeholder:text-gray-400 focus:border-[#102542]"
            />
          </div>
          <span className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#102542]/5 px-4 py-3 text-xs font-semibold text-[#102542] whitespace-nowrap">
            <Users className="w-3.5 h-3.5" aria-hidden />
            {t("tarifasIncluye", {
              pasajeros: tarifasTaxi.pasajeros_incluidos,
            })}
          </span>
        </div>

        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg">
          <div className="max-h-[30rem] overflow-y-auto">
            <table
              id="tabla-tarifas-taxi"
              className="w-full border-collapse text-left text-sm"
            >
              <thead className="sticky top-0 z-10 bg-[#102542] text-white">
                <tr>
                  <th scope="col" className="px-5 py-3 font-semibold">
                    {t("tarifasColDestino")}
                  </th>
                  <th
                    scope="col"
                    className="px-5 py-3 text-right font-semibold whitespace-nowrap"
                  >
                    {t("tarifasColPrecio")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtradas.map((tarifa) => (
                  <tr
                    key={tarifa.destino.es}
                    className="border-t border-gray-100 odd:bg-gray-50/60 hover:bg-[#ffb800]/10 transition-colors"
                  >
                    <th
                      scope="row"
                      className="px-5 py-3 font-medium text-[#102542] text-left"
                    >
                      {tarifa.destino[idioma]}
                    </th>
                    <td className="px-5 py-3 text-right font-bold text-[#102542] tabular-nums whitespace-nowrap">
                      ${formatearPrecio(tarifa.precio)}
                    </td>
                  </tr>
                ))}

                {filtradas.length === 0 && (
                  <tr>
                    <td
                      colSpan={2}
                      className="px-5 py-10 text-center text-sm text-gray-500"
                    >
                      {t("tarifasSinResultados")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <p className="mt-4 text-xs leading-relaxed text-gray-500">
          {t("tarifasNota", { moneda: tarifasTaxi.moneda })}
        </p>
      </div>
    </section>
  );
}
